/**
 * The chat of the portal.
 *
 * Three surfaces which share one state. The bubble in the header opens a
 * panel listing the rooms, the corner of the screen holds the rooms somebody
 * actually opened, and a dialog opens a new one or changes an old one - that
 * last one lives in `chatDialog.ts`, which this module hands the work to.
 *
 * Every page of the portal is a fresh document, so which rooms are open is
 * remembered in `localStorage` and put back on the next page. Without it a
 * conversation would end every time somebody clicked a link, which is not a
 * chat but a page with a text box on it.
 *
 * What arrives while somebody is reading arrives through an `EventSource`:
 * the server holds a stream open and writes into it, the browser reconnects
 * by itself when the connection drops and says where it got to. A browser
 * which has no `EventSource`, or a proxy which will not let the stream
 * through, falls back to asking every twenty seconds - later, never wrong.
 *
 * Deliberately absent: any notion of somebody typing. It is the one thing a
 * chat can say about a person who has not decided to say anything yet.
 */

import { faceWithPresence } from "../core/avatar";
import { relativeDate } from "../core/dates";
import { text } from "../core/i18n";
import { query, queryAll } from "../core/dom";
import { getJson, postJson } from "../core/http";
import { toast } from "../core/toast";
import { closeChatDialog, openChatDialog } from "./chatDialog";
import { setMenuOpen } from "./userMenu";
import type {
    ChatDockState,
    ChatEventResponse,
    ChatMessageResponse,
    ChatMessagesResponse,
    ChatOverviewResponse,
    ChatStateResponse,
    ChatSummaryResponse,
} from "../core/types";

const BASE_URL = "/api/chat";

/** Where the panel of the header gets its rooms from. */
export const OVERVIEW_URL = BASE_URL;

/** Where the stream of live updates is opened. */
export const STREAM_URL = `${BASE_URL}/stream`;

/** Key the open rooms are remembered under between two pages. */
export const DOCK_KEY = "techcraft.chat.dock";

/** How often the rooms are asked for when the stream is not available. */
export const FALLBACK_INTERVAL = 20_000;

/** How many messages one page of a window carries. */
export const PAGE_SIZE = 40;

/** How close to the bottom counts as "reading the end of the room". */
const STICKY_PIXELS = 80;

/** How much air is left between the corner and whatever sits above it. */
const LIFT_GAP = 10;

/**
 * How many rooms may be open in the corner at once.
 *
 * Three is what fits beside a page without becoming the page. Opening a
 * fourth closes the one which has been open the longest - the alternative is
 * a strip of tabs which grows until it covers what somebody came to read.
 */
export const OPEN_LIMIT = 3;

const ERROR_KEY = "js.chat.error";
const ERROR_FALLBACK = "Konverzácie sa nepodarilo načítať.";

/** Return the endpoint holding the messages of the room `id`. */
export function messagesUrl(id: number, before?: number): string {
    const page = `${BASE_URL}/${id}/messages?limit=${PAGE_SIZE}`;

    return before === undefined ? page : `${page}&before=${before}`;
}

/** Return the endpoint one message of the room `id` is written to. */
export function sayUrl(id: number): string {
    return `${BASE_URL}/${id}/messages`;
}

/** Return the endpoint marking the room `id` as read. */
export function readUrl(id: number): string {
    return `${BASE_URL}/${id}/read`;
}

/** Return the text of the badge for `unread`. */
export function badgeText(unread: number): string {
    if (unread <= 0) {
        return "0";
    }

    return unread < 100 ? String(unread) : "99+";
}

/** Return the markup of one icon of the sprite, as the macros write it. */
function iconMarkup(name: string, size: number): string {
    return (
        `<svg class="icon" width="${size}" height="${size}" aria-hidden="true">` +
        `<use href="/static/assets/icons/icons.svg#${name}"></use></svg>`
    );
}

/** Return the rooms this browser has open, or an empty dock. */
export function readDock(): ChatDockState {
    const empty: ChatDockState = { open: [], active: null, collapsed: false };

    try {
        const stored = window.localStorage.getItem(DOCK_KEY);
        if (!stored) {
            return empty;
        }

        const parsed = JSON.parse(stored) as Partial<ChatDockState>;
        const open = Array.isArray(parsed.open) ? parsed.open.filter((id) => Number.isFinite(id)) : [];

        return {
            open,
            active: typeof parsed.active === "number" && open.includes(parsed.active) ? parsed.active : (open[0] ?? null),
            collapsed: parsed.collapsed === true,
        };
    } catch {
        // A browser with storage switched off, or a value somebody edited by
        // hand. Neither is worth an error: the dock simply starts empty.
        return empty;
    }
}

/** Remember which rooms are open for the next page. */
export function writeDock(state: ChatDockState): void {
    try {
        window.localStorage.setItem(DOCK_KEY, JSON.stringify(state));
    } catch {
        // Storage is a convenience here, never a requirement.
    }
}

/** Return who is reading the page, out of the dock the server rendered. */
export function readMe(dock: HTMLElement): number | null {
    const me = Number.parseInt(dock.dataset["chatMe"] ?? "", 10);

    return Number.isFinite(me) ? me : null;
}

/** How much of a name the tab and the head of a window show. */
export const NAME_SHOWN = 15;

/**
 * Return `name` cut down to what a tab can hold.
 *
 * The endpoint refuses a longer one, so this only ever catches a room named
 * before the limit was lowered - but a tab strip which one old name pushes
 * off the screen is worth the three characters this costs.
 */
export function shortName(name: string): string {
    return name.length <= NAME_SHOWN ? name : `${name.slice(0, NAME_SHOWN - 1).trimEnd()}…`;
}

/** How large the face beside the name on a message is drawn. */
const FACE_SIZE = 16;

/** Return the wording of how many people are in `summary`. */
export function membersLabel(summary: ChatSummaryResponse): string {
    if (summary.is_global) {
        return text("js.chat.everybody", "Všetci hráči");
    }

    return text("js.chat.members", "{count} členov", { count: summary.members.length });
}

/** Build the row of the panel standing for one room. */
export function renderRoom(summary: ChatSummaryResponse): HTMLElement {
    const row = document.createElement("button");
    row.type = "button";
    row.className = summary.unread > 0 ? "chat-room is-unread" : "chat-room";
    row.dataset["chatRoom"] = String(summary.id);

    const icon = document.createElement("span");
    icon.className = "chat-room__icon";
    icon.innerHTML = iconMarkup(summary.icon, 15);

    const main = document.createElement("span");
    main.className = "chat-room__main";

    const title = document.createElement("span");
    title.className = "chat-room__title";
    title.textContent = summary.name;
    main.appendChild(title);

    const preview = document.createElement("span");
    preview.className = "chat-room__preview";

    // The face of whoever spoke last, in front of what they said. A room
    // nobody has written in yet says so in words, and there is nobody for a
    // face to belong to.
    if (summary.preview_author) {
        preview.appendChild(faceWithPresence(summary.preview_author, FACE_SIZE, summary.preview_author_online));
    }

    // The words are an element of their own rather than loose text: the line
    // is a row now, and a bare text node in a row is not something the
    // ellipsis can be put on.
    const said = document.createElement("span");
    said.className = "chat-room__said";
    said.textContent = summary.preview;
    preview.appendChild(said);
    main.appendChild(preview);

    const meta = document.createElement("span");
    meta.className = "chat-room__meta";
    meta.textContent = summary.last_message_at
        ? `${membersLabel(summary)} • ${relativeDate(summary.last_message_at)}`
        : membersLabel(summary);
    main.appendChild(meta);

    row.append(icon, main);

    if (summary.unread > 0) {
        const badge = document.createElement("span");
        badge.className = "chat-room__badge";
        badge.textContent = badgeText(summary.unread);
        row.appendChild(badge);
    }

    return row;
}

/**
 * Build the bubble standing for one message, as `me` reads it.
 *
 * Whose it is decides the side and the tint, and it is worked out here rather
 * than sent: one message reaches everybody in the room as one frame.
 */
export function renderBubble(message: ChatMessageResponse, me: number | null): HTMLElement {
    const line = document.createElement("li");
    line.className = message.author_id !== null && message.author_id === me ? "chat-line chat-line--mine" : "chat-line";
    line.dataset["chatMessage"] = String(message.id);

    const who = document.createElement("span");
    who.className = "chat-line__who";

    // The face goes with the name rather than beside the bubble: a column of
    // faces down the edge of a conversation is a column of the same face
    // repeated, and the name is what changes.
    who.appendChild(faceWithPresence(message.author, FACE_SIZE, message.author_online));

    const author = document.createElement("strong");
    author.textContent = message.author;
    who.appendChild(author);

    const when = document.createElement("span");
    when.className = "chat-line__when";
    when.textContent = relativeDate(message.created_at);
    who.appendChild(when);

    // Only the messages which did not come from the portal wear a badge. A
    // label on every single line is a label nobody reads any more.
    if (message.origin !== "WEB") {
        const origin = document.createElement("span");
        origin.className = "chat-line__origin";
        origin.innerHTML = iconMarkup(message.origin_icon, 11);

        const label = document.createElement("span");
        label.textContent = message.origin_label;
        origin.appendChild(label);

        who.appendChild(origin);
    }

    const bubble = document.createElement("span");
    bubble.className = "chat-line__bubble";
    bubble.textContent = message.body;

    line.append(who, bubble);

    return line;
}

/** Build the thin line standing for one thing which happened to a room. */
export function renderNote(entry: ChatEventResponse): HTMLElement {
    const line = document.createElement("li");
    line.className = "chat-note";
    line.dataset["chatEvent"] = String(entry.id);

    const icon = document.createElement("span");
    icon.className = "chat-note__icon";
    icon.innerHTML = iconMarkup(entry.icon, 12);

    const said = document.createElement("span");
    said.textContent = `${entry.sentence} • ${relativeDate(entry.created_at)}`;

    line.append(icon, said);

    return line;
}

/** Everything the three surfaces share while a page is open. */
interface Chat {
    readonly menu: HTMLElement | null;
    readonly dock: HTMLElement;
    readonly tabs: HTMLElement;
    readonly windows: HTMLElement;
    rooms: Map<number, ChatSummaryResponse>;
    state: ChatDockState;
    me: number | null;
    /** What a window says instead of a box while the reader is muted. */
    silence: string;
    stream: EventSource | null;
    timer: number | undefined;
}

/** Write `unread` into the bubble of the header. */
function paintBadge(chat: Chat, unread: number): void {
    if (chat.menu === null) {
        return;
    }

    const badge = query<HTMLElement>("[data-chat-badge]", chat.menu);
    if (badge) {
        badge.textContent = badgeText(unread);
        badge.classList.toggle("is-empty", unread <= 0);
    }

    const counter = query<HTMLElement>("[data-chat-count]", chat.menu);
    if (counter) {
        counter.textContent =
            unread > 0
                ? text("js.chat.unread", "{count} neprečítaných", { count: unread })
                : text("js.chat.all_read", "Všetko prečítané");
    }

    const toggle = query<HTMLElement>("[data-chat-toggle]", chat.menu);
    toggle?.setAttribute(
        "aria-label",
        unread > 0
            ? text("js.chat.aria_unread", "Konverzácie, neprečítaných: {count}", { count: unread })
            : text("js.chat.aria", "Konverzácie"),
    );
}

/** Replace the rows of the panel with what the server last said. */
function paintRooms(chat: Chat): void {
    if (chat.menu === null) {
        return;
    }

    const list = query<HTMLElement>("[data-chat-list]", chat.menu);
    if (!list) {
        return;
    }

    list.innerHTML = "";

    if (chat.rooms.size === 0) {
        const empty = document.createElement("p");
        empty.className = "chat-panel__empty";
        empty.textContent = text("js.chat.empty", "Zatiaľ nemáte žiadne konverzácie.");
        list.appendChild(empty);

        return;
    }

    for (const summary of chat.rooms.values()) {
        list.appendChild(renderRoom(summary));
    }
}

/** Return the window of the room `id`, or `null` when it is not open. */
function windowOf(chat: Chat, id: number): HTMLElement | null {
    return query<HTMLElement>(`[data-chat-window="${id}"]`, chat.windows);
}

/** Return whether the log of `frame` is scrolled to its end. */
function atEnd(frame: HTMLElement): boolean {
    const log = query<HTMLElement>("[data-chat-log]", frame);
    if (!log) {
        return true;
    }

    return log.scrollHeight - log.scrollTop - log.clientHeight <= STICKY_PIXELS;
}

/** Scroll the log of `frame` to the newest message. */
function scrollToEnd(frame: HTMLElement): void {
    const log = query<HTMLElement>("[data-chat-log]", frame);
    if (log) {
        log.scrollTop = log.scrollHeight;
    }
}

/** Build an empty window for `summary`. */
function buildWindow(summary: ChatSummaryResponse): HTMLElement {
    const frame = document.createElement("section");
    frame.className = "chat-window";
    frame.dataset["chatWindow"] = String(summary.id);
    frame.setAttribute("role", "tabpanel");

    const head = document.createElement("header");
    head.className = "chat-window__head";
    head.innerHTML =
        `<span class="chat-window__title">${iconMarkup(summary.icon, 14)}<span data-chat-window-name></span></span>` +
        `<span class="chat-window__actions">` +
        `<button type="button" class="chat-window__action" data-chat-edit hidden>${iconMarkup("edit", 13)}</button>` +
        `<button type="button" class="chat-window__action" data-chat-collapse>${iconMarkup("chevron-down", 13)}</button>` +
        `<button type="button" class="chat-window__action" data-chat-close>${iconMarkup("close", 13)}</button>` +
        `</span>`;

    const log = document.createElement("ol");
    log.className = "chat-window__log";
    log.dataset["chatLog"] = "";

    const compose = document.createElement("form");
    compose.className = "chat-window__compose";
    compose.dataset["chatCompose"] = "";
    compose.innerHTML =
        `<textarea rows="1" maxlength="2000" data-chat-input></textarea>` +
        `<button type="submit" class="chat-window__send" data-chat-send>${iconMarkup("send", 15)}</button>`;

    // Where the box stood while the reader is muted. Built with every window
    // and shown or hidden by what the listing says, so a mute which runs out
    // while a window is open gives the box back without the window moving.
    const silence = document.createElement("p");
    silence.className = "chat-window__muted";
    silence.dataset["chatMuted"] = "";
    silence.hidden = true;

    frame.append(head, log, compose, silence);

    return frame;
}

/** Write the name, the buttons and the wording of `summary` into `frame`. */
function dressWindow(frame: HTMLElement, summary: ChatSummaryResponse): void {
    const name = query<HTMLElement>("[data-chat-window-name]", frame);
    if (name) {
        name.textContent = shortName(summary.name);
        name.title = summary.name;
    }

    const edit = query<HTMLButtonElement>("[data-chat-edit]", frame);
    if (edit) {
        edit.hidden = !summary.can_manage;
        edit.setAttribute("aria-label", text("js.chat.settings", "Nastavenia konverzácie"));
    }

    query<HTMLElement>("[data-chat-collapse]", frame)?.setAttribute(
        "aria-label",
        text("js.chat.collapse", "Zbaliť"),
    );
    query<HTMLElement>("[data-chat-close]", frame)?.setAttribute("aria-label", text("js.chat.close", "Zavrieť"));
    query<HTMLElement>("[data-chat-send]", frame)?.setAttribute("aria-label", text("js.chat.send", "Odoslať"));

    const input = query<HTMLTextAreaElement>("[data-chat-input]", frame);
    if (input) {
        input.placeholder = text("js.chat.placeholder", "Napíšte správu…");
        input.setAttribute("aria-label", text("js.chat.placeholder", "Napíšte správu…"));
    }
}

/** Put one message into the log of `frame`, unless it is already there. */
function appendMessage(frame: HTMLElement, message: ChatMessageResponse, me: number | null): boolean {
    const log = query<HTMLElement>("[data-chat-log]", frame);
    if (!log || query(`[data-chat-message="${message.id}"]`, log)) {
        return false;
    }

    log.appendChild(renderBubble(message, me));

    return true;
}

/** Draw a whole page of a room into `frame`. */
function fillWindow(frame: HTMLElement, payload: ChatMessagesResponse, me: number | null): void {
    const log = query<HTMLElement>("[data-chat-log]", frame);
    if (!log) {
        return;
    }

    log.innerHTML = "";

    if (payload.has_more) {
        const older = document.createElement("li");
        older.className = "chat-window__older";
        older.innerHTML = `<button type="button" data-chat-older></button>`;
        const button = query<HTMLButtonElement>("button", older);
        if (button) {
            button.textContent = text("js.chat.older", "Načítať staršie");
        }

        log.appendChild(older);
    }

    if (payload.items.length === 0 && payload.events.length === 0) {
        const empty = document.createElement("li");
        empty.className = "chat-window__empty";
        empty.textContent = text("js.chat.no_messages", "Zatiaľ tu nikto nič nenapísal.");
        log.appendChild(empty);
    }

    for (const drawn of woven(payload, me)) {
        log.appendChild(drawn);
    }

    scrollToEnd(frame);
}

/**
 * Return the messages and the history of `payload` in the order they happened.
 *
 * An entry of the history belongs where it was written rather than at one end
 * of the window: it is what turns "Eva wrote this" and "Eva was added" into a
 * conversation which can be read once, top to bottom. It also means an entry
 * stays where it is instead of walking down the window every time somebody
 * says something.
 *
 * A page which has older messages above it only shows the entries which fall
 * inside it. The rest belong to the messages which are not drawn yet, and
 * hanging them above the page would date the page wrongly.
 */
export function woven(payload: ChatMessagesResponse, me: number | null): HTMLElement[] {
    const oldest = payload.items[0]?.created_at;
    const within = payload.has_more && oldest !== undefined
        ? payload.events.filter((entry) => entry.created_at >= oldest)
        : payload.events;

    const drawn: { at: number; node: HTMLElement }[] = [
        ...payload.items.map((message) => ({ at: Date.parse(message.created_at), node: renderBubble(message, me) })),
        ...within.map((entry) => ({ at: Date.parse(entry.created_at), node: renderNote(entry) })),
    ];

    return drawn.sort((first, second) => first.at - second.at).map((entry) => entry.node);
}

/** Return the identifier of the newest message drawn in `frame`. */
function newestIn(frame: HTMLElement): number | null {
    const drawn = queryAll<HTMLElement>("[data-chat-message]", frame);
    const last = drawn[drawn.length - 1];
    if (!last) {
        return null;
    }

    const id = Number.parseInt(last.dataset["chatMessage"] ?? "", 10);

    return Number.isFinite(id) ? id : null;
}

/** Draw the strip of tabs above the windows. */
function paintTabs(chat: Chat): void {
    chat.tabs.innerHTML = "";

    for (const id of chat.state.open) {
        const summary = chat.rooms.get(id);
        if (!summary) {
            continue;
        }

        const tab = document.createElement("button");
        tab.type = "button";
        tab.className = id === chat.state.active && !chat.state.collapsed ? "chat-tab is-active" : "chat-tab";
        tab.dataset["chatTab"] = String(id);
        tab.setAttribute("role", "tab");
        tab.setAttribute("aria-selected", String(id === chat.state.active && !chat.state.collapsed));
        tab.innerHTML = `${iconMarkup(summary.icon, 13)}<span></span>`;

        const label = query<HTMLElement>("span", tab);
        if (label) {
            label.textContent = shortName(summary.name);
        }

        tab.title = summary.name;

        if (summary.unread > 0) {
            const badge = document.createElement("span");
            badge.className = "chat-tab__badge";
            badge.textContent = badgeText(summary.unread);
            tab.appendChild(badge);
        }

        chat.tabs.appendChild(tab);
    }

    chat.dock.hidden = chat.state.open.length === 0;
}

/**
 * Tell the rest of the page how tall the corner has become.
 *
 * The toasts and the button which scrolls back to the top live in the same
 * corner, and something they cover is something nobody can press. They read
 * this one property and sit above whatever it says - which is nothing at all
 * when no room is open, a tab strip when one is put away, and a whole window
 * when one is being read.
 */
function measureDock(chat: Chat): void {
    const height = chat.dock.hidden ? 0 : Math.round(chat.dock.getBoundingClientRect().height);

    document.documentElement.style.setProperty("--chat-lift", height > 0 ? `${height + LIFT_GAP}px` : "0px");
}

/** Show the window of the active room and hide the rest. */
/**
 * Take the box away from every window while the reader is muted.
 *
 * Taken away rather than greyed: a box which cannot be sent is a box
 * somebody types into first and finds out about afterwards. The sentence
 * standing in its place says why and until when, so the window never looks
 * broken.
 */
export function paintSilence(windows: ParentNode, notice: string): void {
    for (const frame of queryAll<HTMLElement>("[data-chat-window]", windows)) {
        const compose = query<HTMLElement>("[data-chat-compose]", frame);
        const silence = query<HTMLElement>("[data-chat-muted]", frame);

        if (compose) {
            compose.hidden = notice !== "";
        }

        if (silence) {
            silence.hidden = notice === "";
            silence.textContent = notice;
        }
    }
}

function paintDock(chat: Chat): void {
    paintTabs(chat);
    paintSilence(chat.windows, chat.silence);

    for (const frame of queryAll<HTMLElement>("[data-chat-window]", chat.windows)) {
        const id = Number.parseInt(frame.dataset["chatWindow"] ?? "", 10);
        frame.hidden = chat.state.collapsed || id !== chat.state.active;
    }

    measureDock(chat);
}

/** Mark the room `id` read up to what its window is showing. */
async function markRead(chat: Chat, id: number): Promise<void> {
    const frame = windowOf(chat, id);
    const newest = frame ? newestIn(frame) : null;

    const state = await postJson<ChatStateResponse>(readUrl(id), { message_id: newest });

    const summary = chat.rooms.get(id);
    if (summary) {
        chat.rooms.set(id, { ...summary, unread: state.chat_unread });
    }

    paintBadge(chat, state.unread);
    paintRooms(chat);
    paintTabs(chat);
}

/** Ask the server for the rooms of the reader and redraw everything. */
async function refresh(chat: Chat): Promise<void> {
    const payload = await getJson<ChatOverviewResponse>(OVERVIEW_URL);

    chat.me = payload.me;
    chat.silence = payload.muted_notice ?? "";
    chat.rooms = new Map(payload.items.map((item) => [item.id, item]));

    // A room somebody was taken out of is no longer in the answer, so its
    // window goes with it rather than sitting there refusing to load.
    const open = chat.state.open.filter((id) => chat.rooms.has(id));
    if (open.length !== chat.state.open.length) {
        for (const frame of queryAll<HTMLElement>("[data-chat-window]", chat.windows)) {
            const id = Number.parseInt(frame.dataset["chatWindow"] ?? "", 10);
            if (!chat.rooms.has(id)) {
                frame.remove();
            }
        }

        chat.state = {
            open,
            active: chat.state.active !== null && open.includes(chat.state.active) ? chat.state.active : (open[0] ?? null),
            collapsed: chat.state.collapsed,
        };
        writeDock(chat.state);
    }

    paintBadge(chat, payload.unread);
    paintRooms(chat);

    for (const frame of queryAll<HTMLElement>("[data-chat-window]", chat.windows)) {
        const id = Number.parseInt(frame.dataset["chatWindow"] ?? "", 10);
        const summary = chat.rooms.get(id);
        if (summary) {
            dressWindow(frame, summary);
        }
    }

    paintDock(chat);
}

/** Fetch a page of the room `id` into its window. */
async function loadRoom(chat: Chat, id: number, before?: number): Promise<void> {
    const frame = windowOf(chat, id);
    if (!frame) {
        return;
    }

    const payload = await getJson<ChatMessagesResponse>(messagesUrl(id, before));

    chat.rooms.set(id, payload.chat);
    dressWindow(frame, payload.chat);

    if (before === undefined) {
        fillWindow(frame, payload, chat.me);

        return;
    }

    // Older messages go above what is already drawn, and the log is nudged
    // back to where the reader was: a list which jumps when it grows at the
    // top is a list nobody can read backwards.
    const log = query<HTMLElement>("[data-chat-log]", frame);
    if (!log) {
        return;
    }

    const before_height = log.scrollHeight;
    query<HTMLElement>(".chat-window__older", log)?.remove();

    const older = document.createDocumentFragment();
    if (payload.has_more) {
        const more = document.createElement("li");
        more.className = "chat-window__older";
        more.innerHTML = `<button type="button" data-chat-older></button>`;
        const button = query<HTMLButtonElement>("button", more);
        if (button) {
            button.textContent = text("js.chat.older", "Načítať staršie");
        }

        older.appendChild(more);
    }

    for (const message of payload.items) {
        older.appendChild(renderBubble(message, chat.me));
    }

    log.prepend(older);
    log.scrollTop += log.scrollHeight - before_height;
}

/** Open the room `id` in the dock and make it the one being read. */
async function openRoom(chat: Chat, id: number): Promise<void> {
    const summary = chat.rooms.get(id);
    if (!summary) {
        return;
    }

    // The list has done its job, and leaving it open would leave it hanging
    // over the very window it was used to open.
    if (chat.menu !== null) {
        setMenuOpen(chat.menu, false);
    }

    if (!windowOf(chat, id)) {
        chat.windows.appendChild(buildWindow(summary));
    }

    const wanted = chat.state.open.includes(id) ? [...chat.state.open] : [...chat.state.open, id];

    // The oldest one leaves, and its window with it. The room is not closed
    // for anybody - it is one press away in the panel, unread and all.
    while (wanted.length > OPEN_LIMIT) {
        const dropped = wanted.shift();
        if (dropped !== undefined) {
            windowOf(chat, dropped)?.remove();
        }
    }

    chat.state = { open: wanted, active: id, collapsed: false };
    writeDock(chat.state);
    paintDock(chat);

    try {
        await loadRoom(chat, id);
        await markRead(chat, id);
    } catch {
        toast(text(ERROR_KEY, ERROR_FALLBACK), "error");
    }

    query<HTMLTextAreaElement>("[data-chat-input]", windowOf(chat, id) ?? chat.windows)?.focus();
}

/** Take the room `id` out of the dock. */
function closeRoom(chat: Chat, id: number): void {
    windowOf(chat, id)?.remove();

    const open = chat.state.open.filter((other) => other !== id);
    chat.state = {
        open,
        active: chat.state.active === id ? (open[open.length - 1] ?? null) : chat.state.active,
        collapsed: chat.state.collapsed,
    };
    writeDock(chat.state);
    paintDock(chat);
}

/** Write what was typed into the room `id`. */
async function say(chat: Chat, id: number, body: string): Promise<void> {
    const frame = windowOf(chat, id);
    if (!frame) {
        return;
    }

    const message = await postJson<ChatMessageResponse>(sayUrl(id), { body });

    query<HTMLElement>(".chat-window__empty", frame)?.remove();
    if (appendMessage(frame, message, chat.me)) {
        scrollToEnd(frame);
    }

    await refresh(chat);
}

/** Take in one message which arrived while somebody was reading. */
async function receive(chat: Chat, message: ChatMessageResponse): Promise<void> {
    const frame = windowOf(chat, message.chat_id);

    if (frame) {
        const sticky = atEnd(frame);
        query<HTMLElement>(".chat-window__empty", frame)?.remove();

        if (appendMessage(frame, message, chat.me) && sticky) {
            scrollToEnd(frame);
        }
    }

    const reading =
        frame !== null &&
        !frame.hidden &&
        chat.state.active === message.chat_id &&
        document.visibilityState === "visible";

    if (reading) {
        await markRead(chat, message.chat_id);

        return;
    }

    await refresh(chat);
}

/** Wire the buttons of the panel, the dock and the windows. */
function wire(chat: Chat): void {
    chat.menu?.addEventListener("click", (event: Event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        if (target.closest("[data-chat-new]")) {
            event.preventDefault();
            openChatDialog(null, () => void refresh(chat));

            return;
        }

        const row = target.closest<HTMLElement>("[data-chat-room]");
        if (!row) {
            return;
        }

        const id = Number.parseInt(row.dataset["chatRoom"] ?? "", 10);
        if (Number.isFinite(id)) {
            void openRoom(chat, id);
        }
    });

    chat.tabs.addEventListener("click", (event: Event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const tab = target.closest<HTMLElement>("[data-chat-tab]");
        if (!tab) {
            return;
        }

        const id = Number.parseInt(tab.dataset["chatTab"] ?? "", 10);
        if (!Number.isFinite(id)) {
            return;
        }

        // Pressing the tab of the room which is already open puts it away,
        // which is the only thing left for that press to mean.
        if (chat.state.active === id && !chat.state.collapsed) {
            chat.state = { ...chat.state, collapsed: true };
        } else {
            chat.state = { open: chat.state.open, active: id, collapsed: false };
        }

        writeDock(chat.state);
        paintDock(chat);

        if (!chat.state.collapsed) {
            void markRead(chat, id).catch(() => undefined);
        }
    });

    chat.windows.addEventListener("click", (event: Event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }

        const frame = target.closest<HTMLElement>("[data-chat-window]");
        if (!frame) {
            return;
        }

        const id = Number.parseInt(frame.dataset["chatWindow"] ?? "", 10);
        if (!Number.isFinite(id)) {
            return;
        }

        if (target.closest("[data-chat-close]")) {
            closeRoom(chat, id);

            return;
        }

        if (target.closest("[data-chat-collapse]")) {
            chat.state = { ...chat.state, collapsed: true };
            writeDock(chat.state);
            paintDock(chat);

            return;
        }

        if (target.closest("[data-chat-edit]")) {
            const summary = chat.rooms.get(id);
            if (summary) {
                openChatDialog(summary, () => {
                    void refresh(chat).then(() => loadRoom(chat, id));
                });
            }

            return;
        }

        if (target.closest("[data-chat-older]")) {
            const first = query<HTMLElement>("[data-chat-message]", frame);
            const oldest = Number.parseInt(first?.dataset["chatMessage"] ?? "", 10);
            if (Number.isFinite(oldest)) {
                void loadRoom(chat, id, oldest).catch(() => toast(text(ERROR_KEY, ERROR_FALLBACK), "error"));
            }
        }
    });

    chat.windows.addEventListener("submit", (event: Event) => {
        const form = event.target;
        if (!(form instanceof HTMLFormElement) || !form.matches("[data-chat-compose]")) {
            return;
        }

        event.preventDefault();

        const frame = form.closest<HTMLElement>("[data-chat-window]");
        const input = query<HTMLTextAreaElement>("[data-chat-input]", form);
        const id = Number.parseInt(frame?.dataset["chatWindow"] ?? "", 10);

        if (!input || !Number.isFinite(id)) {
            return;
        }

        const body = input.value.trim();
        if (!body) {
            return;
        }

        input.value = "";
        void say(chat, id, body).catch(() => {
            // Nothing typed is ever thrown away: the box is filled back in
            // so that the answer is one more press rather than retyped.
            input.value = body;
            toast(text("js.chat.not_sent", "Správu sa nepodarilo odoslať."), "error");
        });
    });

    // Enter sends, Shift+Enter starts a line. The box is a textarea because
    // a message may be two sentences, not because it wants a submit button.
    chat.windows.addEventListener("keydown", (event: Event) => {
        if (!(event instanceof KeyboardEvent) || event.key !== "Enter" || event.shiftKey) {
            return;
        }

        const input = event.target;
        if (!(input instanceof HTMLTextAreaElement) || !input.matches("[data-chat-input]")) {
            return;
        }

        event.preventDefault();
        input.form?.requestSubmit();
    });
}

/** Hold the stream open, or fall back to asking on a timer. */
function watch(chat: Chat): void {
    const look = (): void => {
        if (document.visibilityState === "visible") {
            void refresh(chat).catch(() => undefined);
        }
    };

    if (typeof EventSource !== "function") {
        chat.timer = window.setInterval(look, FALLBACK_INTERVAL);
        document.addEventListener("visibilitychange", look);

        return;
    }

    const stream = new EventSource(STREAM_URL);
    chat.stream = stream;

    // Said on every connection, the first one and every reconnection after a
    // drop. Asking for the listing here is what closes the gap: whatever
    // happened while the stream was down is in the answer.
    stream.addEventListener("ready", () => void refresh(chat).catch(() => undefined));

    stream.addEventListener("message", (event: Event) => {
        if (!(event instanceof MessageEvent)) {
            return;
        }

        try {
            void receive(chat, JSON.parse(String(event.data)) as ChatMessageResponse);
        } catch {
            void refresh(chat).catch(() => undefined);
        }
    });

    // Something happened *to* a room - it was renamed, somebody was added or
    // taken out. The listing is the truth about that, so it is asked again
    // rather than patched from the entry.
    stream.addEventListener("history", () => {
        void refresh(chat)
            .then(() => (chat.state.active === null ? undefined : loadRoom(chat, chat.state.active)))
            .catch(() => undefined);
    });

    // A stream which cannot be held open is not an error anybody should be
    // shown: the browser reconnects on its own, and the timer underneath
    // keeps the rooms fresh in the meantime.
    stream.addEventListener("error", () => undefined);

    chat.timer = window.setInterval(look, FALLBACK_INTERVAL * 3);
    document.addEventListener("visibilitychange", look);
}

/** Wire the chat of the current page. */
export function initChat(): void {
    const dock = query<HTMLElement>("[data-chat-dock]");
    const tabs = query<HTMLElement>("[data-chat-tabs]");
    const windows = query<HTMLElement>("[data-chat-windows]");

    if (!dock || !tabs || !windows) {
        return;
    }

    const chat: Chat = {
        menu: query<HTMLElement>("[data-chat-menu]"),
        dock,
        tabs,
        windows,
        rooms: new Map(),
        state: readDock(),
        me: readMe(dock),
        silence: "",
        stream: null,
        timer: undefined,
    };

    wire(chat);

    void refresh(chat)
        .then(async () => {
            for (const id of chat.state.open.slice(-OPEN_LIMIT)) {
                const summary = chat.rooms.get(id);
                if (!summary) {
                    continue;
                }

                chat.windows.appendChild(buildWindow(summary));
                await loadRoom(chat, id);
            }

            paintDock(chat);

            if (chat.state.active !== null && !chat.state.collapsed) {
                await markRead(chat, chat.state.active);
            }
        })
        .catch(() => undefined);

    watch(chat);

    window.addEventListener("resize", () => measureDock(chat));

    window.addEventListener("pagehide", () => {
        chat.stream?.close();
        closeChatDialog();

        if (chat.timer !== undefined) {
            window.clearInterval(chat.timer);
            chat.timer = undefined;
        }
    });
}
